import { buildSumFormula, columnIndexToLetter, getNumberFormat, hexToArgb, PARAM_CELLS, } from './formula-builder';
import { formulaToExcel } from '../utils/formula-utils';
// ============================================================
// Public API
// ============================================================
/**
 * Renders compiled RenderRows into an ExcelJS worksheet.
 *
 * This converts the renderer-agnostic intermediate representation
 * into Excel-specific formulas, formatting, and cell references.
 */
export function renderToExcel(sheet, rows, layoutSettings, theme) {
    const negFmt = theme.negativeFormat;
    // ── Pass 1: Render all rows, build rowId → excelRow mapping ──
    const rowIdToExcelRow = new Map();
    let excelRow = 1;
    let columnWidthsApplied = false;
    for (const renderRow of rows) {
        // Apply column widths from the first column_headers row we encounter
        if (renderRow.kind === 'column_headers' && !columnWidthsApplied) {
            applyColumnWidths(sheet, renderRow.cells);
            columnWidthsApplied = true;
        }
        rowIdToExcelRow.set(renderRow.rowId, excelRow);
        switch (renderRow.kind) {
            case 'title_block':
                renderTitleBlockExcel(sheet, renderRow, excelRow, rows, negFmt);
                break;
            case 'column_headers':
                renderColumnHeadersExcel(sheet, renderRow, excelRow, negFmt);
                applyColumnWidths(sheet, renderRow.cells);
                break;
            case 'header':
                renderHeaderExcel(sheet, renderRow, excelRow, rows, negFmt);
                break;
            case 'data':
            case 'subtotal':
            case 'calculated':
            case 'grand_total':
                renderDataRowExcel(sheet, renderRow, excelRow, layoutSettings, negFmt);
                break;
            case 'spacer':
                break;
            case 'line':
                renderLineExcel(sheet, renderRow, excelRow, rows);
                break;
        }
        excelRow++;
    }
    // ── Pass 2: Resolve row-reference formulas ──
    for (const renderRow of rows) {
        const rowNum = rowIdToExcelRow.get(renderRow.rowId);
        if (rowNum === undefined)
            continue;
        for (let cellIdx = 0; cellIdx < renderRow.cells.length; cellIdx++) {
            const cell = renderRow.cells[cellIdx];
            if (cell.value.kind !== 'formula')
                continue;
            const instruction = cell.value.instruction;
            const colLetter = columnIndexToLetter(cellIdx);
            const excelCell = sheet.getCell(rowNum, cellIdx + 1);
            if (instruction.type === 'sum_rows' && instruction.rowRefs) {
                const resolvedRows = resolveRowRefs(instruction.rowRefs, rowIdToExcelRow);
                if (resolvedRows.length > 0) {
                    excelCell.value = { formula: buildSumFormula(colLetter, resolvedRows) };
                }
            }
            else if (instruction.type === 'row_arithmetic' && instruction.userFormula) {
                const excelFormula = formulaToExcel(instruction.userFormula, rowIdToExcelRow, colLetter);
                excelCell.value = { formula: excelFormula };
            }
            else if (instruction.type === 'variance_amount') {
                const varFormula = buildVarianceAmountFormula(renderRow, rowNum);
                if (varFormula) {
                    excelCell.value = { formula: varFormula };
                }
            }
            else if (instruction.type === 'variance_percent') {
                const varFormula = buildVariancePercentFormula(renderRow, rowNum);
                if (varFormula) {
                    excelCell.value = { formula: varFormula };
                }
            }
            else {
                // Account-level formulas (clm_balance, clm_budget, etc.)
                const formula = instructionToExcelFormula(instruction);
                if (formula) {
                    excelCell.value = { formula };
                }
            }
        }
    }
    // ── Layout settings ──
    if (layoutSettings.showGridlines === false) {
        sheet.views = [{ showGridLines: false }];
    }
    if (layoutSettings.repeatHeaderOnPrint) {
        const firstHeaderIdx = rows.findIndex((r) => r.kind === 'column_headers');
        if (firstHeaderIdx >= 0) {
            const headerExcelRow = rowIdToExcelRow.get(rows[firstHeaderIdx].rowId);
            if (headerExcelRow !== undefined) {
                sheet.pageSetup = { ...sheet.pageSetup, printTitlesRow: `1:${headerExcelRow}` };
            }
        }
    }
}
// ============================================================
// Row renderers
// ============================================================
function renderTitleBlockExcel(sheet, renderRow, excelRow, allRows, negFmt) {
    const cell = renderRow.cells[0];
    if (!cell)
        return;
    const colCount = getMaxColumnCount(allRows);
    if (colCount > 1) {
        sheet.mergeCells(excelRow, 1, excelRow, colCount);
    }
    const excelCell = sheet.getCell(excelRow, 1);
    const text = cell.value.kind === 'text' ? cell.value.text : '';
    const formula = resolveTokensToFormula(text);
    if (formula) {
        excelCell.value = { formula };
    }
    else {
        excelCell.value = text;
    }
    applyCellStyle(excelCell, cell.style, negFmt);
}
function renderColumnHeadersExcel(sheet, renderRow, excelRow, negFmt) {
    for (let i = 0; i < renderRow.cells.length; i++) {
        const cell = renderRow.cells[i];
        const excelCell = sheet.getCell(excelRow, i + 1);
        excelCell.value = cell.value.kind === 'text' ? cell.value.text : '';
        applyCellStyle(excelCell, cell.style, negFmt);
    }
}
function renderHeaderExcel(sheet, renderRow, excelRow, allRows, negFmt) {
    const cell = renderRow.cells[0];
    if (!cell)
        return;
    const colCount = getMaxColumnCount(allRows);
    if (colCount > 1) {
        sheet.mergeCells(excelRow, 1, excelRow, colCount);
    }
    const excelCell = sheet.getCell(excelRow, 1);
    excelCell.value = cell.value.kind === 'text' ? cell.value.text : '';
    applyCellStyle(excelCell, cell.style, negFmt);
}
function renderDataRowExcel(sheet, renderRow, excelRow, layoutSettings, negFmt) {
    const indent = layoutSettings.labelIndent === 'space'
        ? ' '.repeat(renderRow.depth * layoutSettings.spacesPerLevel)
        : '';
    for (let i = 0; i < renderRow.cells.length; i++) {
        const cell = renderRow.cells[i];
        const excelCell = sheet.getCell(excelRow, i + 1);
        if (cell.value.kind === 'text') {
            const isLabel = cell.columnDef.type === 'label';
            excelCell.value = isLabel ? indent + cell.value.text : cell.value.text;
        }
        else if (cell.value.kind === 'number') {
            excelCell.value = cell.value.value;
        }
        // formula values handled in pass 2
        applyCellStyle(excelCell, cell.style, negFmt);
    }
}
function renderLineExcel(sheet, renderRow, excelRow, allRows) {
    const colCount = getMaxColumnCount(allRows);
    const borderStyle = 'thin';
    const border = { style: borderStyle, color: { argb: '000000' } };
    for (let i = 0; i < colCount; i++) {
        const cell = sheet.getCell(excelRow, i + 1);
        cell.border = { bottom: border };
    }
    const rowObj = sheet.getRow(excelRow);
    rowObj.height = renderRow.rowHeight ?? 6;
}
// ============================================================
// Formula conversion
// ============================================================
function instructionToExcelFormula(instruction) {
    const code = instruction.accountCode;
    switch (instruction.type) {
        case 'clm_balance':
            return code
                ? `CLM.GL_BALANCE("${code}",${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        case 'clm_prior_period':
            return code
                ? `CLM.GL_BALANCE("${code}",EOMONTH(${PARAM_CELLS.periodEnd},-1),${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        case 'clm_prior_year':
            return code
                ? `CLM.GL_BALANCE("${code}",DATE(YEAR(${PARAM_CELLS.periodEnd})-1,MONTH(${PARAM_CELLS.periodEnd}),DAY(${PARAM_CELLS.periodEnd})),${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        case 'clm_budget':
            return code
                ? `CLM.BUDGET_AMOUNT("${instruction.budgetName ?? ''}","${code}",${PARAM_CELLS.periodStart},${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        case 'clm_ytd':
            return code
                ? `CLM.GL_SUM("${code}",${PARAM_CELLS.fiscalYearStart},${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        case 'clm_period_offset': {
            const offset = instruction.periodOffset ?? 0;
            return code
                ? `CLM.GL_BALANCE("${code}",EOMONTH(${PARAM_CELLS.periodEnd},${-Math.abs(offset)}),${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        }
        case 'clm_year_offset': {
            const offset = instruction.periodOffset ?? 0;
            return code
                ? `CLM.GL_BALANCE("${code}",DATE(YEAR(${PARAM_CELLS.periodEnd})-${Math.abs(offset)},MONTH(${PARAM_CELLS.periodEnd}),DAY(${PARAM_CELLS.periodEnd})),${PARAM_CELLS.company},${PARAM_CELLS.connection})`
                : null;
        }
        case 'literal':
            return null;
        default:
            return null;
    }
}
function resolveVarianceColumns(renderRow, varianceType) {
    // Try explicit column IDs from the variance instruction
    const varCell = renderRow.cells.find((c) => c.value.kind === 'formula' && c.value.instruction.type === varianceType);
    if (varCell?.value.kind === 'formula') {
        const { baseColumnId, compareColumnId } = varCell.value.instruction;
        if (baseColumnId && compareColumnId) {
            const baseIdx = renderRow.cells.findIndex((c) => c.columnDef.id === baseColumnId);
            const compIdx = renderRow.cells.findIndex((c) => c.columnDef.id === compareColumnId);
            if (baseIdx >= 0 && compIdx >= 0) {
                return { baseIdx, compIdx };
            }
        }
    }
    // Fall back to auto-detect (backward compatibility)
    const baseIdx = renderRow.cells.findIndex((c) => c.columnDef.type === 'current');
    const compIdx = renderRow.cells.findIndex((c) => c.columnDef.type === 'budget' || c.columnDef.type === 'prior_period');
    return { baseIdx, compIdx };
}
function buildVarianceAmountFormula(renderRow, excelRow) {
    const { baseIdx, compIdx } = resolveVarianceColumns(renderRow, 'variance_amount');
    if (baseIdx < 0 || compIdx < 0)
        return '0';
    const baseCol = columnIndexToLetter(baseIdx);
    const compCol = columnIndexToLetter(compIdx);
    return `${baseCol}${excelRow}-${compCol}${excelRow}`;
}
function buildVariancePercentFormula(renderRow, excelRow) {
    const { baseIdx, compIdx } = resolveVarianceColumns(renderRow, 'variance_percent');
    if (baseIdx < 0 || compIdx < 0)
        return '0';
    const baseCol = columnIndexToLetter(baseIdx);
    const compCol = columnIndexToLetter(compIdx);
    return `IF(${compCol}${excelRow}=0,0,(${baseCol}${excelRow}-${compCol}${excelRow})/ABS(${compCol}${excelRow}))`;
}
// ============================================================
// Token resolution for title blocks
// ============================================================
function resolveTokensToFormula(text) {
    const tokenPattern = /\{(Company|Period)\}/gi;
    if (!tokenPattern.test(text))
        return null;
    tokenPattern.lastIndex = 0;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = tokenPattern.exec(text)) !== null) {
        const before = text.slice(lastIndex, match.index);
        if (before)
            parts.push(`"${before}"`);
        const tokenName = match[1].toLowerCase();
        if (tokenName === 'company') {
            parts.push(PARAM_CELLS.company);
        }
        else if (tokenName === 'period') {
            parts.push(PARAM_CELLS.period);
        }
        lastIndex = match.index + match[0].length;
    }
    const after = text.slice(lastIndex);
    if (after)
        parts.push(`"${after}"`);
    if (parts.length === 1)
        return parts[0];
    return `CONCATENATE(${parts.join(',')})`;
}
// ============================================================
// Helpers
// ============================================================
function resolveRowRefs(rowRefs, rowIdToExcelRow) {
    const result = [];
    for (const ref of rowRefs) {
        const excelRow = rowIdToExcelRow.get(ref);
        if (excelRow !== undefined) {
            result.push(excelRow);
        }
    }
    return result;
}
function getMaxColumnCount(rows) {
    let max = 0;
    for (const row of rows) {
        if (row.kind === 'column_headers' && row.cells.length > max) {
            max = row.cells.length;
        }
    }
    return max > 0 ? max : 1;
}
function applyColumnWidths(sheet, cells) {
    cells.forEach((cell, idx) => {
        const isLabel = cell.columnDef.type === 'label';
        sheet.getColumn(idx + 1).width = isLabel
            ? (cell.columnDef.width ?? 30)
            : (cell.columnDef.width ?? 15);
    });
}
function applyCellStyle(excelCell, style, negFmt) {
    if (!style)
        return;
    // Font
    const font = {};
    let hasFont = false;
    if (style.bold !== undefined) {
        font.bold = style.bold;
        hasFont = true;
    }
    if (style.italic !== undefined) {
        font.italic = style.italic;
        hasFont = true;
    }
    if (style.fontSize !== undefined) {
        font.size = style.fontSize;
        hasFont = true;
    }
    if (style.textColor) {
        font.color = { argb: hexToArgb(style.textColor) };
        hasFont = true;
    }
    if (hasFont) {
        excelCell.font = { ...excelCell.font, ...font };
    }
    // Fill
    if (style.backgroundColor) {
        excelCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(style.backgroundColor) },
        };
    }
    // Alignment
    if (style.alignment) {
        excelCell.alignment = { horizontal: style.alignment };
    }
    // Border
    if (style.borderBottom) {
        const borderStyle = style.borderBottom === 'double' ? 'double'
            : style.borderBottom === 'thick' ? 'thick'
                : style.borderBottom === 'medium' ? 'medium'
                    : 'thin';
        excelCell.border = { ...excelCell.border, bottom: { style: borderStyle } };
    }
    // Number format
    if (style.numberFormat) {
        excelCell.numFmt = getNumberFormat(style.numberFormat, negFmt);
    }
}
//# sourceMappingURL=excel-renderer.js.map